import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientCarFuelEnum,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	CarFormInput,
	CarFormOutput,
} from "web_myplann/Portfolio/Forms/CarForm/carFormSchema";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { UNITS } from "web_myplann/Shared/Utils/units";

type CarFormFieldsProps = {
	control: Control<CarFormInput, unknown, CarFormOutput>;
};

export const CarFormFields = ({ control }: CarFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const today = new Date();

	const fuelOptions = useEnumSelectOptions(
		ClientCarFuelEnum,
		Resources.PortfolioDetail.Property.CarFuelTypeValue,
		[ClientCarFuelEnum.None],
	);

	const vehicleTypeOptions = useEnumSelectOptions(
		ClientPropertyVehicleTypeEnum,
		Resources.PortfolioDetail.Property.CarPropertyTypeValue,
		[ClientPropertyVehicleTypeEnum.None],
	);

	return (
		<>
			<Stack mt={0.8} bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="carBrand"
					label={t(Resources.PortfolioDetail.Property.CarBrand)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="carModel"
					label={t(Resources.Portfolio.AddCar.Fields.model)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormSelect
					control={control}
					name="propertyType"
					label={t(Resources.PortfolioDetail.Property.CarPropertyType)}
					options={vehicleTypeOptions}
				/>
				<FlatRowFormSelect
					control={control}
					name="carFuel"
					label={t(Resources.PortfolioDetail.Property.CarFuelType)}
					options={fuelOptions}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddCar.Sections.identification)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="carLicensePlate"
					label={t(Resources.Portfolio.AddCar.Fields.licensePlate)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="carVin"
					label={t(Resources.PortfolioDetail.Property.CarVin)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddCar.Sections.valueAndDates)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="value"
					type="float"
					label={t(Resources.PortfolioDetail.Property.Value)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix="Kč"
				/>
				<FlatRowFormDatePicker
					control={control}
					name="carPurchaseDate"
					label={t(Resources.PortfolioDetail.Property.CarPurchaseDate)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="carRegistration"
					label={t(Resources.PortfolioDetail.Property.CarRegistration)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					maxDate={today}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="carManufactureMonthYear"
					label={t(Resources.Portfolio.AddCar.Fields.manufactureMonthYear)}
					views={[DateView.Year, DateView.Month]}
					displayFormat={DateFormat.MonthYear}
					maxDate={today}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddCar.Sections.engine)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="carPower"
					type="number"
					label={t(Resources.PortfolioDetail.Property.CarPower)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix={UNITS.kilowatt}
				/>
				<FlatRowFormInput
					control={control}
					name="carVolume"
					type="number"
					label={t(Resources.PortfolioDetail.Property.CarVolume)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix={UNITS.cubicCentimeter}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddCar.Sections.description)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					label={t(Resources.Portfolio.AddCar.Fields.description)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddArt.Sections.investment)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>
		</>
	);
};
